import { useEffect, useState } from "react";
import { api, eur, frDate } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = {
  first_name: "", last_name: "", email: "", phone: "", address: "",
  position: "", social_security: "", contract_type: "CDI",
  hire_date: new Date().toISOString().slice(0, 10),
  gross_monthly_salary: 0, status: "actif", notes: "",
};

export default function Salaries() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/employees").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, gross_monthly_salary: Number(form.gross_monthly_salary) || 0 };
      if (edit) await api.put(`/employees/${edit.id}`, payload);
      else await api.post("/employees", payload);
      toast.success(edit ? "Salarié modifié." : "Salarié ajouté.");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer ${s.first_name} ${s.last_name} ?`)) return;
    await api.delete(`/employees/${s.id}`); load();
  };

  return (
    <div data-testid="salaries-page">
      <PageHeader
        eyebrow="Ressources humaines"
        title="Salarié(e)s"
        description="Fiches des salariés de l'association, utilisées pour générer les bulletins de paie."
        actions={
          <Button data-testid="new-employee-btn" onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} className="bg-brand-orange hover:bg-[#D04A06] text-white">
            <Plus size={16} className="mr-1" /> Nouveau salarié
          </Button>
        }
      />

      <div className="bg-white border border-brand-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 small-caps">
            <tr>
              <th className="text-left py-3 px-4">Salarié(e)</th>
              <th className="text-left py-3 px-4">Poste</th>
              <th className="text-left py-3 px-4">Contrat</th>
              <th className="text-left py-3 px-4">Embauche</th>
              <th className="text-left py-3 px-4">Brut mensuel</th>
              <th className="text-right py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-gray-400">Aucun salarié pour l'instant.</td></tr>}
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-brand-border hover:bg-gray-50">
                <td className="py-3 px-4">
                  <div className="font-medium text-gray-900">{s.first_name} {s.last_name}</div>
                  <div className="text-xs text-gray-500">{s.email}</div>
                </td>
                <td className="py-3 px-4 text-gray-700">{s.position || "-"}</td>
                <td className="py-3 px-4"><Badge variant="outline">{s.contract_type}</Badge></td>
                <td className="py-3 px-4 text-gray-700">{frDate(s.hire_date)}</td>
                <td className="py-3 px-4 font-medium text-brand-navy">{eur(s.gross_monthly_salary)}</td>
                <td className="py-3 px-4 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(s); setForm({...empty, ...s}); setOpen(true); }}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(s)}><Trash2 size={14} className="text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-navy">{edit ? "Modifier" : "Nouveau salarié"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div><Label>Prénom *</Label><Input value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} /></div>
            <div><Label>Nom *</Label><Input value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
            <div><Label>Poste</Label><Input value={form.position} onChange={(e) => setForm({...form, position: e.target.value})} /></div>
            <div><Label>N° Sécurité sociale</Label><Input value={form.social_security} onChange={(e) => setForm({...form, social_security: e.target.value})} /></div>
            <div><Label>Type de contrat</Label>
              <Select value={form.contract_type} onValueChange={(v) => setForm({...form, contract_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CDI">CDI</SelectItem>
                  <SelectItem value="CDD">CDD</SelectItem>
                  <SelectItem value="Stage">Stage</SelectItem>
                  <SelectItem value="Apprentissage">Apprentissage</SelectItem>
                  <SelectItem value="Autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Date d'embauche</Label><Input type="date" value={form.hire_date || ""} onChange={(e) => setForm({...form, hire_date: e.target.value})} /></div>
            <div><Label>Brut mensuel (€)</Label><Input type="number" step="0.01" value={form.gross_monthly_salary} onChange={(e) => setForm({...form, gross_monthly_salary: e.target.value})} /></div>
            <div><Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="actif">Actif</SelectItem>
                  <SelectItem value="inactif">Inactif</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-brand-orange hover:bg-[#D04A06] text-white">{edit ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
